import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Paper, 
  Chip, 
  Button, 
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Beispieldaten für IDs
const sampleIds = [
  { id: 'ID-123', label: 'Blutzucker (mg/dL)', category: 'Labor', description: 'Nüchtern-Blutzuckerwert in Milligramm pro Deziliter' },
  { id: 'ID-345', label: 'Blutdruck systolisch (mmHg)', category: 'Vital', description: 'Systolischer Blutdruckwert in Millimeter Quecksilbersäule' },
  { id: 'ID-678', label: 'Cholesterin (mg/dL)', category: 'Labor', description: 'Gesamtcholesterinwert im Blut' },
  { id: 'ID-9', label: 'Herzfrequenz (bpm)', category: 'Vital', description: 'Herzschläge pro Minute in Ruhe' },
  { id: 'ID-42', label: 'Alter (Jahre)', category: 'Demographie', description: 'Alter des Patienten in Jahren' },
  { id: 'ID-56', label: 'Gewicht (kg)', category: 'Vital', description: 'Körpergewicht in Kilogramm' },
  { id: 'ID-78', label: 'Größe (cm)', category: 'Vital', description: 'Körpergröße in Zentimetern' },
  { id: 'ID-90', label: 'BMI', category: 'Berechnet', description: 'Body Mass Index (kg/m²)' },
  { id: 'ID-101', label: 'HbA1c (%)', category: 'Labor', description: 'Glykiertes Hämoglobin in Prozent' },
  { id: 'ID-202', label: 'Kreatinin (mg/dL)', category: 'Labor', description: 'Nierenfunktionsmarker im Blut' },
  { id: 'ID-303', label: 'Geschlecht', category: 'Demographie', description: 'Biologisches Geschlecht (m/w/d)' },
  { id: 'ID-404', label: 'Raucherstatus', category: 'Lebensstil', description: 'Aktueller Raucherstatus (ja/nein/ehemals)' },
];

// Kategorien für die Filterung
const categories = [...new Set(sampleIds.map(item => item.category))];

const DataVariables = ({ onSaveVariables, savedVariableSets = [] }) => {
  const [variableSetName, setVariableSetName] = useState('');
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customVariableId, setCustomVariableId] = useState('');
  const [customVariableLabel, setCustomVariableLabel] = useState('');
  const [error, setError] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showUploadSection, setShowUploadSection] = useState(false);

  // Filtern der IDs basierend auf Suchbegriff und Kategorie
  const filteredIds = sampleIds.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.label.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === '' || item.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Hinzufügen einer Variable zur Auswahl
  const addVariable = (variable) => {
    if (!selectedVariables.some(v => v.id === variable.id)) {
      setSelectedVariables([...selectedVariables, variable]);
    }
  };

  // Entfernen einer Variable aus der Auswahl
  const removeVariable = (variableId) => {
    setSelectedVariables(selectedVariables.filter(v => v.id !== variableId));
  };

  // Hinzufügen einer benutzerdefinierten Variable
  const addCustomVariable = () => {
    if (!customVariableId.trim()) {
      setError('Bitte geben Sie eine ID für die benutzerdefinierte Variable ein');
      return;
    }

    if (selectedVariables.some(v => v.id === customVariableId)) {
      setError('Diese ID ist bereits in Ihrer Auswahl vorhanden');
      return;
    }

    const newVariable = {
      id: customVariableId,
      label: customVariableLabel || customVariableId,
      category: 'Benutzerdefiniert',
      description: 'Vom Benutzer definierte Variable'
    };

    setSelectedVariables([...selectedVariables, newVariable]);
    setCustomVariableId('');
    setCustomVariableLabel('');
    setError('');
  };

  // Speichern des Variablensets
  const saveVariableSet = () => {
    if (!variableSetName.trim()) {
      setError('Bitte geben Sie einen Namen für das Variablenset ein');
      return;
    }

    if (selectedVariables.length === 0) {
      setError('Bitte wählen Sie mindestens eine Variable aus');
      return;
    }

    const variableSet = {
      name: variableSetName,
      variables: selectedVariables
    };

    onSaveVariables(variableSet);
    setError('');
  };

  // Simulierte Datei-Upload-Funktion
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      // In einer realen Anwendung würden wir hier die Datei parsen
      // Für dieses Beispiel fügen wir einfach einige Beispielvariablen hinzu
      setTimeout(() => {
        const newVariables = [
          { id: 'ID-505', label: 'Triglyceride (mg/dL)', category: 'Labor', description: 'Aus Datei importiert' },
          { id: 'ID-606', label: 'HDL-Cholesterin (mg/dL)', category: 'Labor', description: 'Aus Datei importiert' },
          { id: 'ID-707', label: 'LDL-Cholesterin (mg/dL)', category: 'Labor', description: 'Aus Datei importiert' }
        ];
        
        // Füge nur Variablen hinzu, die noch nicht ausgewählt sind
        const newSelectedVariables = [...selectedVariables];
        newVariables.forEach(variable => {
          if (!newSelectedVariables.some(v => v.id === variable.id)) {
            newSelectedVariables.push(variable);
          }
        });
        
        setSelectedVariables(newSelectedVariables);
      }, 1000);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        Schritt 2: Datenvariablen definieren
        <Tooltip title="Wählen Sie die Variablen aus, die Sie analysieren möchten">
          <IconButton size="small">
            <HelpOutlineIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Spezifizieren Sie die Variablen, die in Ihre Analyse einbezogen werden sollen.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Name des Variablensets
              </Typography>
              <TextField
                fullWidth
                label="Name des Variablensets"
                variant="outlined"
                value={variableSetName}
                onChange={(e) => setVariableSetName(e.target.value)}
                placeholder="z.B. Diabetes-Risikofaktoren"
                sx={{ mb: 2 }}
              />
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                Variablen auswählen
                <Tooltip title="Wählen Sie aus den verfügbaren Variablen oder fügen Sie eigene hinzu">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Suche nach Variablen"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="ID oder Name eingeben"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Kategorie filtern</InputLabel>
                    <Select
                      value={selectedCategory}
                      label="Kategorie filtern"
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <MenuItem value="">Alle Kategorien</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>{category}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Kategorie</TableCell>
                      <TableCell>Aktion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIds.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.id}</TableCell>
                        <TableCell>{item.label}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => addVariable(item)}
                            disabled={selectedVariables.some(v => v.id === item.id)}
                            startIcon={<AddIcon />}
                          >
                            Hinzufügen
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredIds.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Keine Variablen gefunden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Benutzerdefinierte Variable hinzufügen
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Variable ID"
                      variant="outlined"
                      value={customVariableId}
                      onChange={(e) => setCustomVariableId(e.target.value)}
                      placeholder="z.B. ID-999"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Variablenname (optional)"
                      variant="outlined"
                      value={customVariableLabel}
                      onChange={(e) => setCustomVariableLabel(e.target.value)}
                      placeholder="z.B. Meine Variable"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={addCustomVariable}
                      startIcon={<AddIcon />}
                    >
                      Hinzufügen
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<UploadFileIcon />}
                onClick={() => setShowUploadSection(!showUploadSection)}
                sx={{ mb: 3 }}
              >
                {showUploadSection ? 'Upload-Bereich ausblenden' : 'Variablen aus Datei importieren'}
              </Button>
              
              {showUploadSection && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    CSV- oder Excel-Datei mit Variablen hochladen
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Die Datei sollte eine Spalte mit dem Namen "ID" und optional eine Spalte "Name" enthalten.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<UploadFileIcon />}
                    >
                      Datei auswählen
                      <input
                        type="file"
                        hidden
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileUpload}
                      />
                    </Button>
                    {uploadedFile && (
                      <Typography variant="body2">
                        {uploadedFile.name} <CheckCircleIcon color="success" fontSize="small" sx={{ verticalAlign: 'middle', ml: 1 }} />
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Ausgewählte Variablen ({selectedVariables.length})
              </Typography>
              
              {selectedVariables.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                  Keine Variablen ausgewählt
                </Typography>
              ) : (
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedVariables.map((variable) => (
                    <Chip
                      key={variable.id}
                      label={`${variable.id} (${variable.label})`}
                      onDelete={() => removeVariable(variable.id)}
                      color="primary"
                      variant="outlined"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={saveVariableSet}
                  disabled={!variableSetName || selectedVariables.length === 0}
                >
                  Variablenset speichern
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Gespeicherte Variablensets
              </Typography>
              
              {savedVariableSets.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Keine gespeicherten Variablensets vorhanden
                </Typography>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {savedVariableSets.map((set, index) => (
                    <Chip
                      key={index}
                      label={`${set.name} (${set.variables.length})`}
                      color="primary"
                      variant="outlined"
                      onClick={() => {
                        setVariableSetName(set.name);
                        setSelectedVariables(set.variables);
                      }}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Hilfe & Tipps
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Variablenauswahl:</strong> Wählen Sie die Variablen aus, die Sie in Ihrer Analyse verwenden möchten.
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Benutzerdefinierte Variablen:</strong> Fügen Sie eigene Variablen hinzu, wenn diese nicht in der Liste vorhanden sind.
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Datei-Import:</strong> Laden Sie eine CSV- oder Excel-Datei hoch, um mehrere Variablen auf einmal zu importieren.
              </Typography>
              
              <Typography variant="body2">
                <strong>Tipp:</strong> Speichern Sie Ihre Variablensets, um sie für zukünftige Anfragen wiederzuverwenden.
              </Typography>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Variablenkategorien
              </Typography>
              
              <List dense>
                {categories.map((category) => (
                  <ListItem key={category} disablePadding>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <DataObjectIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={category} 
                      secondary={`${sampleIds.filter(item => item.category === category).length} Variablen`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataVariables;
